import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight,
  Check,
  Mail,
  Globe,
  Package,
  User,
  Phone,
  Loader2,
  CalendarClock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { track, getAttribution, trackCtaClick } from '../lib/analytics';
import { suppressExitIntent } from '../lib/exitIntent';

/**
 * Contact section — primary homepage lead capture.
 *
 * Replaces the old "Calendly first, long form second" flow with a 4-field
 * QuickQuote form (Name, Email, Website, Monthly Orders). Decision rationale:
 *
 *   - Microsoft Clarity showed dead-clicks + rage-clicks on the inline
 *     Calendly CTA. Cold paid traffic doesn't want to pick a calendar slot
 *     before they trust us.
 *   - Industry benchmark: cold paid → Calendly-first booking rate is
 *     ~0.5–1.5%. Cold paid → 3-4 field lead form converts at 5–12%.
 *   - Calendly now lives on its own `/book` route, surfaced as the secondary
 *     CTA both on this thank-you screen and in the header nav.
 *
 * Backend: posts to `/api/leads/quick-quote` which emails the team and sends
 * a branded confirmation back to the prospect via Resend.
 */

const ORDER_BUCKETS = [
  '<500',
  '500–2,000',
  '2,000–10,000',
  '10,000–50,000',
  '50,000+',
];

function QuickQuoteForm({ onSubmitted }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    monthlyOrders: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Form-abandonment tracking — keeps the funnel reports we wired into GA4
  // working after the form simplification. Refs not state so we don't
  // re-render on every keystroke.
  const startedRef = useRef(false);
  const completedFieldsRef = useRef(new Set());
  const submittedRef = useRef(false);
  const lastFieldRef = useRef(null);

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (!startedRef.current) {
      startedRef.current = true;
      track('form_start', { form_id: 'quick_quote' });
    }
    lastFieldRef.current = field;
    if (value && !completedFieldsRef.current.has(field)) {
      completedFieldsRef.current.add(field);
      track('form_field_complete', { form_id: 'quick_quote', field });
    }
  };

  useEffect(() => {
    const flush = () => {
      if (!startedRef.current || submittedRef.current) return;
      track('form_abandon', {
        form_id: 'quick_quote',
        fields_completed: completedFieldsRef.current.size,
        last_field_touched: lastFieldRef.current,
      });
      submittedRef.current = true;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.website || !form.monthlyOrders) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/leads/quick-quote`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...form, attribution: getAttribution() }),
        },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || 'Failed to submit');
      }
      // Conversion event — `quick_quote_submit` is the new primary GA4/Ads
      // soft conversion. value=100 sits below book_call=500 so Smart Bidding
      // still ranks booked calls higher.
      track('quick_quote_submit', {
        monthly_orders: form.monthlyOrders,
        value: 100,
        currency: 'USD',
      });
      submittedRef.current = true;
      suppressExitIntent();
      onSubmitted?.(form);
    } catch (err) {
      toast.error(
        err.message || 'Something went wrong. Please try again or email info@shipmasterprep.com.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-indigo-600" aria-hidden="true" />
      <form onSubmit={handleSubmit} className="p-7 sm:p-9" data-testid="quick-quote-form">
        <div className="mb-7">
          <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            Get a Custom Quote
          </h3>
          <p className="mt-1.5 text-slate-600">
            Four quick fields. A real human replies within 24 hours with your numbers &mdash; no
            sales call required.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            icon={User}
            label="First name"
            value={form.name}
            onChange={(v) => update('name', v)}
            placeholder="Jane"
            testid="quick-quote-name"
          />
          <Field
            icon={Mail}
            type="email"
            label="Work email"
            value={form.email}
            onChange={(v) => update('email', v)}
            placeholder="jane@yourbrand.com"
            testid="quick-quote-email"
          />
        </div>
        <div className="mt-4">
          <Field
            icon={Globe}
            label="Website or Shopify URL"
            value={form.website}
            onChange={(v) => update('website', v)}
            placeholder="yourbrand.com"
            testid="quick-quote-website"
          />
        </div>
        <div className="mt-4">
          <Field
            icon={Phone}
            type="tel"
            label="Phone"
            optional
            value={form.phone}
            onChange={(v) => update('phone', v)}
            placeholder="(555) 123-4567"
            helper="Only used if email follow-up gets stuck."
            testid="quick-quote-phone"
          />
        </div>
        <div className="mt-4">
          <Label className="text-sm font-semibold text-slate-800 mb-1.5 flex items-center gap-1.5">
            <Package className="w-4 h-4 text-slate-500" />
            Monthly order volume
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {ORDER_BUCKETS.map((bucket) => {
              const active = form.monthlyOrders === bucket;
              return (
                <button
                  type="button"
                  key={bucket}
                  onClick={() => update('monthlyOrders', bucket)}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all duration-150 ${
                    active
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                  }`}
                  data-testid={`quick-quote-orders-${bucket}`}
                >
                  {bucket}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="mt-7 w-full h-12 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          data-testid="quick-quote-submit"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Sending...
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              Get My Custom Quote
              <ArrowRight className="w-4 h-4" />
            </span>
          )}
        </Button>

        <p className="mt-4 text-center text-xs text-slate-500">
          No spam. We&apos;ll reply within 24 hours with a real quote.
        </p>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', icon: Icon, testid, optional, helper }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-800 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        {label}
        {optional && <span className="ml-1 text-xs font-medium text-slate-400">(optional)</span>}
      </Label>
      <Input
        type={type}
        inputMode={type === 'tel' ? 'tel' : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-11 rounded-lg"
        data-testid={testid}
      />
      {helper && <p className="mt-1 text-xs text-slate-500">{helper}</p>}
    </div>
  );
}

function ThanksScreen({ name, email, onReset }) {
  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-indigo-200 ring-1 ring-indigo-200 p-8 sm:p-10 text-center"
      data-testid="quick-quote-thanks"
    >
      <div className="w-16 h-16 mx-auto rounded-full bg-indigo-600 flex items-center justify-center mb-4">
        <Check className="w-8 h-8 text-white" strokeWidth={3} />
      </div>
      <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2">
        Thanks{name ? <>, <span data-clarity-mask="true">{name}</span></> : ''}!
      </h3>
      <p className="text-slate-600 max-w-md mx-auto">
        Your quote request is in. We&apos;ll review your details and reply to{' '}
        <span data-clarity-mask="true" className="font-semibold text-slate-900">
          {email}
        </span>{' '}
        within 24 hours.
      </p>

      <div className="mt-7 p-5 bg-indigo-50/60 border border-indigo-100 rounded-xl">
        <p className="text-base font-bold text-slate-900 mb-1">
          Want answers faster?
        </p>
        <p className="text-sm text-slate-600 mb-4">
          Grab a 15-minute slot with us — we&apos;ll review your numbers live.
        </p>
        <Button
          asChild
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-11 px-6 text-sm font-semibold"
          data-testid="quick-quote-book-cta"
        >
          <Link
            to="/book"
            onClick={() =>
              trackCtaClick({
                label: 'Book a 15-min call',
                location: 'contact_thanks',
                destination: '/book',
              })
            }
            className="inline-flex items-center gap-2"
          >
            <CalendarClock className="w-4 h-4" />
            Book a 15-min call
          </Link>
        </Button>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="mt-6 text-sm text-slate-500 hover:text-indigo-600 font-medium transition-colors"
      >
        Submit another request
      </button>
    </div>
  );
}

export default function Contact() {
  const [submittedInfo, setSubmittedInfo] = useState(null);

  const handleSubmitted = (data) => {
    setSubmittedInfo({ name: data.name, email: data.email });
    toast.success('Quote request sent!');
  };

  const handleReset = () => {
    setSubmittedInfo(null);
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="contact" className="py-20 sm:py-28 brand-soft-bg">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Get Your Quote in 24 Hours
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Four quick fields. No sales call required &mdash; we&apos;ll reply by email with real
            numbers.
          </p>
        </div>

        {submittedInfo ? (
          <ThanksScreen
            name={submittedInfo.name}
            email={submittedInfo.email}
            onReset={handleReset}
          />
        ) : (
          <>
            <QuickQuoteForm onSubmitted={handleSubmitted} />

            <div className="text-center mt-8">
              <p className="text-sm text-slate-500 inline-flex flex-wrap items-center justify-center gap-1.5">
                Prefer to hop on a quick call instead?
                <Link
                  to="/book"
                  className="inline-flex items-center gap-1 font-semibold text-indigo-600 hover:underline"
                  data-testid="contact-book-call-link"
                  onClick={() =>
                    trackCtaClick({
                      label: 'Book a Call',
                      location: 'contact_secondary',
                      destination: '/book',
                    })
                  }
                >
                  <CalendarClock className="w-4 h-4" />
                  Book a Call
                </Link>
              </p>
            </div>
          </>
        )}

        <p className="mt-10 text-center text-slate-600">
          Prefer to reach out directly?{' '}
          <a
            href="mailto:info@shipmasterprep.com"
            className="font-semibold text-indigo-600 hover:underline"
          >
            info@shipmasterprep.com
          </a>
        </p>
      </div>
    </section>
  );
}
